import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { analyticsApi } from '@/api';
import {
  Alert,
  Card,
  CardHeader,
  ErrorState,
  Icon,
  LinkButton,
  LoadingState,
  MetricCard,
  PageHeader,
} from '@/components/ui';
import { PeriodPicker } from '@/features/analytics/PeriodPicker';
import { TrendChart } from '@/features/analytics/TrendChart';
import { PIPELINE_SERIES } from '@/features/analytics/series';
import { count, days, percent } from '@/features/analytics/format';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { cn } from '@/utils/cn';

/** What each stage of the journey means, in the student's words. */
const STAGE_NOTES = {
  applied: 'Applications you sent',
  reviewed: 'A company opened and read it',
  shortlisted: 'Picked for the next step',
  interviewed: 'Invited to at least one interview',
  offered: 'A company made you an offer',
  hired: 'You accepted and joined',
};

/** The four places an application can be right now, as one stacked bar. */
const STANDING = [
  { key: 'placed', label: 'Placed', bar: 'bg-emerald-500', dot: 'bg-emerald-500' },
  { key: 'in_progress', label: 'In progress', bar: 'bg-brand-500', dot: 'bg-brand-500' },
  { key: 'rejected', label: 'Not taken forward', bar: 'bg-amber-400', dot: 'bg-amber-400' },
  { key: 'withdrawn', label: 'You withdrew', bar: 'bg-slate-300', dot: 'bg-slate-300' },
];

/**
 * The student's own search, and nothing else.
 *
 * There is deliberately no comparison against other candidates: a benchmark a
 * student cannot act on is discouragement rather than information. Every
 * figure comes from the student's own applications; the suggestions are
 * rules over those figures, not generated text.
 */
export function StudentInsightsPage() {
  const [period, setPeriod] = useState(90);

  const {
    data: insights,
    isPending,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['student-insights', period],
    queryFn: () => analyticsApi.getStudentInsights(period),
  });

  const header = (
    <PageHeader
      title="Application analytics"
      description="How your applications are moving, from the first one you send to an accepted offer."
      action={<PeriodPicker value={period} onChange={setPeriod} />}
    />
  );

  if (isPending) {
    return (
      <div>
        {header}
        <LoadingState label="Looking at your applications…" />
      </div>
    );
  }
  if (isError) {
    return (
      <div>
        {header}
        <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
      </div>
    );
  }

  const { totals, speed, exits, funnel = [] } = insights;
  const stage = Object.fromEntries(funnel.map((entry) => [entry.key, entry]));
  const offerRate = stage.offered?.rate_from_top;
  const interviewRate = stage.interviewed?.rate_from_top;
  const placed = stage.hired?.count ?? totals.hires;

  return (
    <div>
      {header}

      {totals.applications === 0 && (
        <Alert tone="info" className="mb-6" title="Nothing to report yet">
          You have not applied to anything in this period. Apply to a few internships and this page
          starts showing how they are doing.{' '}
          <Link to={paths.internships} className="font-medium underline underline-offset-2">
            Find internships
          </Link>
        </Alert>
      )}

      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Applications sent"
          icon="application"
          value={count(totals.applications)}
          hint={`In the last ${period} days`}
        />
        <MetricCard
          label="Interview rounds"
          icon="calendar"
          tone="info"
          value={count(totals.interviews)}
          hint={
            interviewRate != null
              ? `${percent(interviewRate)} of applications reached an interview`
              : 'Rounds companies invited you to'
          }
        />
        <MetricCard
          label="Offers received"
          icon="document"
          tone="warning"
          value={count(totals.offers)}
          hint={offerRate != null ? `${percent(offerRate)} of applications` : 'Offers made to you'}
        />
        <MetricCard
          label="Placements"
          icon="check"
          tone="success"
          value={count(totals.hires)}
          hint="Offers you accepted"
        />
      </div>

      <div className="mb-6 grid gap-6 lg:grid-cols-[1.25fr_1fr]">
        <Card>
          <CardHeader
            title="Your application journey"
            description="How far the applications you sent in this period got. Each bar is the share of all your applications that reached that stage."
          />
          <Journey funnel={funnel} />
        </Card>

        <div className="space-y-6">
          <Card>
            <CardHeader
              title="Company response"
              description="How companies are reacting to your applications."
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <RateRing value={speed.response_rate} label="Answered" />
              <div className="border-line rounded-xl border p-4">
                <p className="text-ink-muted text-xs font-medium tracking-wide uppercase">
                  First reply
                </p>
                <p className="text-ink mt-1 text-2xl font-semibold tracking-tight">
                  {days(speed.median_days_to_first_response)}
                </p>
                <p className="text-ink-muted mt-1 text-xs leading-5">
                  Typical wait before a company reviews, shortlists or replies.
                </p>
              </div>
            </div>
            <p className="text-ink-muted mt-3 text-xs leading-5">
              “Answered” counts every application a company has acted on — reviewed, shortlisted,
              interviewed, offered or declined.
            </p>
          </Card>

          <Card>
            <CardHeader
              title="Where your applications are now"
              description="Every application from this period, by where it stands today."
            />
            <Standing values={{ placed, ...exits }} />
          </Card>
        </div>
      </div>

      <Card className="mb-6">
        <CardHeader title="Suggested next steps" description="Based on the numbers above." />
        <NextSteps insights={insights} stage={stage} />
      </Card>

      <Card>
        <CardHeader
          title="Activity over time"
          description="When you applied, and the interviews, offers and placements that followed."
        />
        <TrendChart
          points={insights.trend}
          series={PIPELINE_SERIES}
          granularity={insights.period?.granularity}
          emptyLabel="You have not applied in this period"
        />
      </Card>
    </div>
  );
}

/** The ladder as numbered steps, each with its share of all applications. */
function Journey({ funnel }) {
  const top = funnel[0]?.count ?? 0;
  if (top === 0) {
    return <p className="text-ink-muted text-sm">No applications in this period.</p>;
  }

  // Where most applications stopped: the step that lost the most of them.
  let biggest = null;
  funnel.forEach((entry, index) => {
    if (index === 0) return;
    const lost = funnel[index - 1].count - entry.count;
    if (lost > 0 && (!biggest || lost > biggest.lost)) {
      biggest = { lost, from: funnel[index - 1].label, to: entry.label };
    }
  });

  return (
    <div>
      <ol className="space-y-3.5">
        {funnel.map((entry, index) => {
          const share = top ? Math.round((entry.count / top) * 100) : 0;
          const reached = entry.count > 0;
          return (
            <li key={entry.key} className="flex items-center gap-3">
              <span
                className={cn(
                  'grid size-7 shrink-0 place-items-center rounded-full text-xs font-semibold',
                  reached ? 'bg-brand-600 text-white' : 'bg-surface-muted text-ink-muted',
                )}
              >
                {index + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-3">
                  <p className="text-ink text-sm font-medium">
                    {entry.label}
                    <span className="text-ink-muted ml-2 hidden text-xs font-normal sm:inline">
                      {STAGE_NOTES[entry.key]}
                    </span>
                  </p>
                  <p className="text-ink shrink-0 text-sm font-semibold tabular-nums">
                    {count(entry.count)}
                    <span className="text-ink-muted ml-1.5 text-xs font-normal">{share}%</span>
                  </p>
                </div>
                <div className="bg-surface-muted mt-1.5 h-2 overflow-hidden rounded-full">
                  <div
                    className={cn(
                      'h-full rounded-full',
                      entry.key === 'hired' ? 'bg-emerald-500' : 'bg-brand-500',
                    )}
                    style={{ width: `${share}%` }}
                  />
                </div>
              </div>
            </li>
          );
        })}
      </ol>
      <p className="bg-surface-muted/70 text-ink-muted mt-5 flex items-start gap-2 rounded-lg px-3 py-2.5 text-xs leading-5">
        <Icon name="info" size={14} className="text-brand-600 mt-0.5 shrink-0" />
        {biggest
          ? `Most applications stopped between ${biggest.from} and ${biggest.to} (${biggest.lost} ${biggest.lost === 1 ? 'application' : 'applications'}). That is the step to work on.`
          : 'Every application reached every stage so far. Keep going.'}
      </p>
    </div>
  );
}

/** A percentage as a ring, or a dash when there was nothing to measure. */
function RateRing({ value, label }) {
  const radius = 26;
  const circumference = 2 * Math.PI * radius;
  const known = value != null;
  return (
    <div className="border-line flex items-center gap-4 rounded-xl border p-4">
      <div className="relative size-16 shrink-0">
        <svg viewBox="0 0 64 64" className="size-full -rotate-90" aria-hidden="true">
          <circle cx="32" cy="32" r={radius} fill="none" strokeWidth="6" className="stroke-line" />
          {known && (
            <circle
              cx="32"
              cy="32"
              r={radius}
              fill="none"
              strokeWidth="6"
              strokeLinecap="round"
              className="stroke-brand-500"
              strokeDasharray={circumference}
              strokeDashoffset={circumference * (1 - value / 100)}
            />
          )}
        </svg>
        <span className="text-ink absolute inset-0 grid place-items-center text-sm font-semibold">
          {percent(value)}
        </span>
      </div>
      <div>
        <p className="text-ink-muted text-xs font-medium tracking-wide uppercase">{label}</p>
        <p className="text-ink mt-1 text-xs leading-5">
          {known
            ? value >= 100
              ? 'Companies have acted on every application.'
              : 'Share of your applications a company has acted on.'
            : 'Nothing to measure yet.'}
        </p>
      </div>
    </div>
  );
}

/** Placed, in progress, not taken forward and withdrawn, as one bar. */
function Standing({ values }) {
  const total = STANDING.reduce((sum, item) => sum + (values[item.key] ?? 0), 0);
  return (
    <div>
      <div className="bg-surface-muted flex h-3 overflow-hidden rounded-full">
        {total > 0 &&
          STANDING.map((item) =>
            values[item.key] ? (
              <span
                key={item.key}
                className={item.bar}
                style={{ width: `${(values[item.key] / total) * 100}%` }}
                title={`${item.label}: ${values[item.key]}`}
              />
            ) : null,
          )}
      </div>
      <dl className="mt-4 grid grid-cols-2 gap-3">
        {STANDING.map((item) => (
          <div key={item.key} className="border-line rounded-lg border px-3 py-2.5">
            <dt className="text-ink-muted flex items-center gap-1.5 text-xs">
              <span className={cn('size-2 rounded-full', item.dot)} />
              {item.label}
            </dt>
            <dd className="text-ink mt-0.5 text-lg font-semibold tabular-nums">
              {count(values[item.key])}
            </dd>
          </div>
        ))}
      </dl>
      <p className="text-ink-muted mt-3 text-xs">“You withdrew” includes offers you turned down.</p>
    </div>
  );
}

/** Suggestions share the row evenly, so one or two never leave a gap. */
const STEP_COLUMNS = { 1: 'grid-cols-1', 2: 'md:grid-cols-2', 3: 'md:grid-cols-3' };

/**
 * Up to three things worth doing, chosen by rules over the student's own
 * figures. Deterministic: the same numbers always give the same advice.
 */
function NextSteps({ insights, stage }) {
  const { totals, speed, exits, by_status: byStatus = {} } = insights;
  const steps = [];
  const add = (step) => steps.length < 3 && steps.push(step);

  if ((byStatus.OFFERED ?? 0) > 0) {
    add({
      icon: 'contract',
      tone: 'success',
      title: 'An offer is waiting for your answer',
      text: 'Read the terms and accept or decline before the respond-by date.',
      to: paths.student.offers,
      action: 'Open offers',
    });
  }
  if (totals.applications === 0) {
    add({
      icon: 'search',
      title: 'Start applying',
      text: 'Nothing has been sent in this period. Roles picked for your profile are the quickest place to start.',
      to: paths.student.recommendations,
      action: 'See roles for you',
    });
  }
  if (speed.response_rate != null && speed.response_rate < 50) {
    add({
      icon: 'fileText',
      title: 'Most applications have no reply yet',
      text: 'A clear, complete CV gets read sooner. See what to improve on yours.',
      to: paths.student.cv,
      action: 'Score my CV',
    });
  }
  const reviewed = stage.reviewed?.count ?? 0;
  const shortlisted = stage.shortlisted?.count ?? 0;
  if (reviewed >= 3 && shortlisted / reviewed < 0.3) {
    add({
      icon: 'target',
      title: 'Companies read your applications, but few shortlist',
      text: 'Tailor your CV and cover letter to each role, and apply where your fit is strongest.',
      to: paths.student.recommendations,
      action: 'Best-fit roles',
    });
  }
  if ((stage.interviewed?.count ?? 0) > 0 && (stage.offered?.count ?? 0) === 0) {
    add({
      icon: 'route',
      title: 'Interviews are not turning into offers yet',
      text: 'Your career guidance lists the skills companies in your field ask for most.',
      to: paths.student.career,
      action: 'Career guidance',
    });
  }
  if (exits.in_progress > 0) {
    add({
      icon: 'clipboard',
      title: `${exits.in_progress} ${exits.in_progress === 1 ? 'application is' : 'applications are'} still moving`,
      text: 'Keep an eye on interview invitations and messages from companies.',
      to: paths.student.applications,
      action: 'Track applications',
    });
  }
  if (steps.length === 0) {
    add({
      icon: 'sparkles',
      title: 'Keep the momentum',
      text: 'Students who apply steadily get more interviews. New roles that fit you appear every week.',
      to: paths.student.recommendations,
      action: 'See roles for you',
    });
  }

  return (
    <ul className={cn('grid gap-3', STEP_COLUMNS[steps.length])}>
      {steps.map((step) => (
        <li key={step.title} className="border-line flex flex-col rounded-xl border p-4">
          <span
            className={cn(
              'grid size-9 place-items-center rounded-lg',
              step.tone === 'success'
                ? 'bg-emerald-50 text-emerald-600'
                : 'bg-brand-50 text-brand-600',
            )}
          >
            <Icon name={step.icon} size={17} />
          </span>
          <p className="text-ink mt-3 text-sm font-semibold">{step.title}</p>
          <p className="text-ink-muted mt-1 flex-1 text-xs leading-5">{step.text}</p>
          <LinkButton to={step.to} variant="secondary" size="sm" className="mt-3 self-start">
            {step.action}
            <Icon name="arrowRight" size={14} />
          </LinkButton>
        </li>
      ))}
    </ul>
  );
}
